import * as React from 'react';
import { useCallback } from 'react';
import { SavedFiltersType, TableSettingsEnumType } from './types';
import { RemoveSavedFiltersModalForm } from './RemoveSavedFiltersModalForm';
import { SaveByNameModalForm } from './SaveByNameModalForm';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import { useNastaveni } from '@mc/react-app-base';
import { showConfirm } from '../modal/modalFunctions';
import { Modal } from '../modal/Modal';

export const GLOBAL_SETTINGS_FILTERS_SAVED_FILTERS_KEY =
  'globalSettings.filters.filters';

export const LAST_FILTER_ID = 'lastFilter';

export type FilterHookProps = {
  savedFilters: SavedFiltersType[];
  removeSavedFilters: (savedFilters: SavedFiltersType[]) => void;
  saveFilters: (saveFilters: any, visibleFields?: string[]) => void;
  saveLastFilter: (filter: any, visibleFields?: string[]) => void;
};

/**
 * Vrací informace a pomocné metody pro filtery
 * @param settingsKey klíč nastavení, nullable jenom z legacy důvodů (je používáno v Filters)
 */
export const useFilters = (settingsKey?: string): FilterHookProps => {
  if (!settingsKey) {
    return {
      savedFilters: [],
      removeSavedFilters: () => {},
      saveFilters: () => {},
      saveLastFilter: () => {},
    };
  }

  const [savedFilters, setSavedFilters] = useNastaveni<SavedFiltersType[]>(
    `${GLOBAL_SETTINGS_FILTERS_SAVED_FILTERS_KEY}.${settingsKey}`,
    []
  );

  const removeSavedFilters = useCallback(
    (savedFilters: SavedFiltersType[]) => {
      Modal.show(({ onClose }) => (
        <RemoveSavedFiltersModalForm
          savedFilters={savedFilters}
          onClose={onClose}
          onSubmit={async (data) => {
            setSavedFilters(data.filtersToKeep);
            onClose();
          }}
          initialValues={{ filtersToKeep: savedFilters }}
        />
      ));
    },
    [setSavedFilters]
  );

  const saveLastFilter = useCallback(
    (formFilter, visibleFields) => {
      setSavedFilters([
        ...savedFilters.filter(
          (item: SavedFiltersType) => item.id !== LAST_FILTER_ID
        ),
        {
          id: LAST_FILTER_ID,
          nazev: 'Poslední hledání',
          filter: formFilter,
          visibleFields: visibleFields,
        },
      ]);
    },
    [savedFilters, setSavedFilters]
  );

  const saveFilters = useCallback(
    (formFilter: any, visibleFields: any) => {
      Modal.show(({ onClose }) => (
        <SaveByNameModalForm
          type={TableSettingsEnumType.FILTER}
          onClose={onClose}
          onSubmit={async (data) => {
            const duplicitFilter = savedFilters.find(
              (item: SavedFiltersType) => item.nazev === data.nazev
            );
            if (duplicitFilter) {
              const allowSubmit = await showConfirm({
                title: 'Filter s daným názvem již existuje',
                message: 'Přejete si jej nahradit?',
              });
              if (allowSubmit) {
                setSavedFilters([
                  ...savedFilters.filter(
                    (item: SavedFiltersType) => item.nazev !== data.nazev
                  ),
                  {
                    id: duplicitFilter.id,
                    nazev: data.nazev,
                    filter: formFilter,
                    visibleFields: visibleFields,
                  },
                ]);
                onClose();
              }
            } else {
              setSavedFilters([
                ...savedFilters,
                {
                  id: uuidv4(),
                  nazev: data.nazev,
                  filter: formFilter,
                  visibleFields: visibleFields,
                },
              ]);
              onClose();
            }
          }}
        />
      ));
    },
    [savedFilters, setSavedFilters]
  );

  return {
    savedFilters: savedFilters,
    removeSavedFilters,
    saveLastFilter,
    saveFilters,
  };
};
